import { useState } from "react";
import { toast } from "sonner";
import { getDepartmentName, getMunicipalityName } from "@pahcertificar/certmanager-locations";
import { useUtilityGenerateCsr } from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input } from "@/components/ui/Input";
import { LocationPicker } from "@/components/ui/LocationPicker";
import { SecretField } from "@/components/ui/SecretField";
import { formatDate } from "@/lib/utils";
import type { UtilityCsrInput, UtilityCsrResponse } from "@/types";

interface FormState {
  departmentCode: string | null;
  municipalityCode: string | null;
  empresa: string;
  area: string;
  nombreComun: string;
}

const DEFAULT: FormState = {
  departmentCode: null,
  municipalityCode: null,
  empresa: "",
  area: "",
  nombreComun: "",
};

export function UtilitiesCsr() {
  const [form, setForm] = useState<FormState>(DEFAULT);
  const [result, setResult] = useState<UtilityCsrResponse | null>(null);
  const mutation = useUtilityGenerateCsr();

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.departmentCode || !form.municipalityCode) {
      toast.error("Select departamento and municipio");
      return;
    }
    const payload: UtilityCsrInput = {
      pais: "CO",
      departamento: getDepartmentName(form.departmentCode) ?? form.departmentCode,
      ciudad: getMunicipalityName(form.municipalityCode) ?? form.municipalityCode,
      empresa: form.empresa,
      area: form.area,
      nombreComun: form.nombreComun,
    };
    try {
      const res = await mutation.mutateAsync(payload);
      setResult(res);
      toast.success("CSR generated");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to generate CSR");
    }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Utilities · Generate CSR</h1>
        <p className="text-sm text-slate-500 mt-1">
          Stateless CSR + private key generation. Nothing is stored on the server.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Subject (Distinguished Name)</CardTitle>
            <CardDescription>All fields map to X.509 subject components.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="País (C)" required hint="Always CO in this system">
              <Input value="CO" readOnly disabled />
            </Field>
            <div className="md:col-span-2">
              <LocationPicker
                departmentCode={form.departmentCode}
                municipalityCode={form.municipalityCode}
                onDepartmentChange={(code) => set("departmentCode", code)}
                onMunicipalityChange={(code) => set("municipalityCode", code)}
                required
              />
            </div>
            <Field label="Empresa (O)" required>
              <Input
                value={form.empresa}
                onChange={(e) => set("empresa", e.target.value)}
                required
                maxLength={200}
              />
            </Field>
            <Field label="Área (OU)" required>
              <Input
                value={form.area}
                onChange={(e) => set("area", e.target.value)}
                required
                maxLength={200}
              />
            </Field>
            <Field label="Nombre común (CN)" required hint="Domain or common name" className="md:col-span-2">
              <Input
                value={form.nombreComun}
                onChange={(e) => set("nombreComun", e.target.value)}
                required
                maxLength={64}
              />
            </Field>
          </CardContent>
        </Card>
        <div className="mt-3">
          <Button type="submit" loading={mutation.isPending}>
            Generate CSR
          </Button>
        </div>
      </form>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Result</CardTitle>
            <CardDescription>
              Save both CSR and private key securely — the key is not stored anywhere.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-slate-400">Subject</p>
                <p className="text-slate-900 font-mono break-all">{result.subject}</p>
              </div>
              <div>
                <p className="text-slate-400">Algorithm / Key size</p>
                <p className="text-slate-900">
                  {result.algorithm} · {result.keySize} bits
                </p>
              </div>
              <div>
                <p className="text-slate-400">Created</p>
                <p className="text-slate-900">{formatDate(result.createdAt)}</p>
              </div>
            </div>
            <SecretField
              label="CSR"
              value={result.csr}
              revealDefault
              multiline
              download={{ filename: "request.csr", mime: "application/pkcs10" }}
            />
            <SecretField
              label="Private key"
              value={result.key}
              multiline
              download={{ filename: "private-key.pem", mime: "application/x-pem-file" }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
